import React, { useState } from "react";
import { Search, ShoppingCart, User, Heart, LogOut, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";

const NAV = [
  { label: "Inicio", target: "top" },
  { label: "Por género", target: "genres" },
];

const Logo = () => (
  <img
    src="/logo-edibrante.png"
    alt="Edibrante · Leer para crecer · Plan Lector Escolar"
    className="h-12 sm:h-14 w-auto select-none"
    draggable="false"
  />
);

export default function Header({ search, onSearchChange, onOpenCart, onNav }) {
  const { count } = useCart();
  const { customer, setAuthOpen, setFavOpen, logout } = useCustomer();
  const [active, setActive] = useState("Inicio");
  const [menuOpen, setMenuOpen] = useState(false);

  const handleAccount = () => {
    if (customer) setMenuOpen((v) => !v);
    else setAuthOpen(true);
  };

  const handleNav = (item) => {
    setActive(item.label);
    if (item.target === "account") { handleAccount(); return; }
    onNav?.(item.target);
  };

  const firstName = customer?.name?.split(" ")[0] || "Mi cuenta";

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[var(--lc-border)]" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center gap-4">
        <button onClick={() => { setActive("Inicio"); onNav?.("top"); }} className="flex items-center shrink-0" data-testid="logo-link">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-7 ml-6">
          {NAV.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNav(item)}
              data-testid={`nav-${item.target}`}
              className={`relative text-[15px] font-semibold transition-colors ${
                active === item.label ? "text-[var(--lc-primary)]" : "text-[var(--lc-ink)] hover:text-[var(--lc-primary)]"
              }`}
            >
              {item.label}
              {active === item.label && (
                <span className="absolute -bottom-1.5 left-0 right-0 h-[3px] rounded-full bg-[var(--lc-primary)]" />
              )}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 hidden md:block max-w-md ml-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[var(--lc-slate)]" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar libros, autores, títulos…"
              className="w-full rounded-full border border-[var(--lc-border)] bg-[var(--lc-cream)] focus:border-[var(--lc-primary)] focus:ring-4 focus:ring-[var(--lc-primary)]/15 outline-none transition-colors pl-11 pr-4 py-2.5 text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 ml-auto md:ml-0 shrink-0">
          <button onClick={onOpenCart} data-testid="open-cart-button" className="relative p-2.5 rounded-full hover:bg-[var(--lc-cream)] text-[var(--lc-ink)] transition-colors">
            <ShoppingCart className="w-6 h-6" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="lc-bounce-in absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 grid place-items-center rounded-full bg-[var(--lc-primary)] text-white text-[11px] font-bold">
                {count}
              </span>
            )}
          </button>

          <div className="relative">
            <button
              onClick={handleAccount}
              data-testid="account-button"
              className="flex items-center gap-1.5 rounded-full hover:bg-[var(--lc-cream)] text-[var(--lc-ink)] transition-colors py-1.5 pl-2.5 pr-2.5"
            >
              <User className="w-6 h-6" />
              {customer && <span className="hidden sm:inline text-sm font-semibold max-w-[110px] truncate">{firstName}</span>}
              {customer && <ChevronDown className="w-4 h-4 hidden sm:inline" />}
            </button>

            {customer && menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl border border-[var(--lc-border)] shadow-xl z-50 overflow-hidden" data-testid="account-menu">
                  <div className="px-4 py-3 border-b border-[var(--lc-border)]">
                    <p className="text-xs text-[var(--lc-slate)]">Sesión iniciada como</p>
                    <p className="font-bold text-[var(--lc-ink)] text-sm truncate">{customer.email}</p>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); setFavOpen(true); }}
                    data-testid="menu-favorites"
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[var(--lc-ink)] hover:bg-[var(--lc-cream)]"
                  >
                    <Heart className="w-4 h-4 text-[var(--lc-primary)]" /> Mis favoritos
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); logout(); }}
                    data-testid="menu-logout"
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[var(--lc-accent)] hover:bg-[var(--lc-accent)]/5"
                  >
                    <LogOut className="w-4 h-4" /> Cerrar sesión
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search */}
      <div className="md:hidden px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[var(--lc-slate)]" />
          <input
            data-testid="search-input-mobile"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar libros, autores, títulos…"
            className="w-full rounded-full border border-[var(--lc-border)] bg-[var(--lc-cream)] outline-none pl-11 pr-4 py-2.5 text-sm"
          />
        </div>
      </div>
    </header>
  );
}
